import { Component, OnInit } from '@angular/core';
import { KnowledgebaseService } from '../../services/knowledgebase.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-questionnaire',
  templateUrl: './questionnaire.component.html',
  styleUrls: ['./questionnaire.component.css']
})
export class QuestionnaireComponent implements OnInit {
  // Form Model
  formData: any = {
    status: '',
    age_group: '',
    institution_name: '',
    campus_region: '',
    field_or_industry: '',
    living_situation: '',
    academic_stressors: [],
    workplace_stressors: [],
    physical_symptoms: [],
    late_night_message: '',
    ideal_response_feedback: '',
    avoid_patterns_feedback: '',
    gad_1: null,
    gad_2: null,
    gad_3: null,
    gad_4: null,
    gad_5: null,
    gad_6: null,
    gad_7: null,
    phq_1: null,
    phq_2: null,
    phq_3: null,
    phq_4: null,
    phq_5: null,
    phq_6: null,
    phq_7: null,
    phq_8: null,
    phq_9: null
  };

  // Live friendly indicators
  friendlyGadLevel = 'Normal / Calm';
  friendlyPhqLevel = 'Steady / Manageable';
  progressPercentage = 0;

  // Submission State
  isSubmitting = false;
  isSubmitted = false;
  submissionRef = '';

  constructor(private kbService: KnowledgebaseService) {}

  ngOnInit(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  onScoreChange(): void {
    // Calculate GAD-7 Sum
    let gadSum = 0;
    for (let i = 1; i <= 7; i++) {
      const val = this.formData[`gad_${i}`];
      if (val !== null && val !== undefined) gadSum += Number(val);
    }

    if (gadSum >= 15) this.friendlyGadLevel = 'High Tension / Overwhelmed';
    else if (gadSum >= 10) this.friendlyGadLevel = 'Noticeable Anxiety';
    else if (gadSum >= 5) this.friendlyGadLevel = 'Mild Nerves';
    else this.friendlyGadLevel = 'Normal / Calm';

    // Calculate PHQ-9 Sum
    let phqSum = 0;
    for (let i = 1; i <= 9; i++) {
      const val = this.formData[`phq_${i}`];
      if (val !== null && val !== undefined) phqSum += Number(val);
    }

    if (phqSum >= 20) this.friendlyPhqLevel = 'Heavy Weight / Very Low';
    else if (phqSum >= 15) this.friendlyPhqLevel = 'Noticeably Drained';
    else if (phqSum >= 10) this.friendlyPhqLevel = 'Moderate Heaviness';
    else if (phqSum >= 5) this.friendlyPhqLevel = 'Mild Blues';
    else this.friendlyPhqLevel = 'Steady / Manageable';

    this.calculateProgress();
  }

  toggleCheckbox(arrayName: string, value: string): void {
    const arr: string[] = this.formData[arrayName];
    const index = arr.indexOf(value);
    if (index > -1) {
      arr.splice(index, 1);
    } else {
      arr.push(value);
    }
  }

  isChecked(arrayName: string, value: string): boolean {
    return this.formData[arrayName].includes(value);
  }

  calculateProgress(): void {
    let filled = 0;
    const requiredKeys = [
      'status', 'age_group', 'institution_name', 'campus_region', 'field_or_industry', 'living_situation',
      'gad_1', 'gad_2', 'gad_3', 'gad_4', 'gad_5', 'gad_6', 'gad_7',
      'phq_1', 'phq_2', 'phq_3', 'phq_4', 'phq_5', 'phq_6', 'phq_7', 'phq_8', 'phq_9',
      'late_night_message', 'ideal_response_feedback', 'avoid_patterns_feedback'
    ];

    requiredKeys.forEach(k => {
      const v = this.formData[k];
      if (v !== null && v !== undefined && String(v).trim() !== '') {
        filled++;
      }
    });

    this.progressPercentage = Math.min(100, Math.round((filled / requiredKeys.length) * 100));
  }

  submitQuestionnaire(): void {
    this.calculateProgress();
    if (this.progressPercentage < 100) {
      Swal.fire({
        icon: 'info',
        title: 'A few questions left',
        text: 'Please take a moment to answer all required questions so Joy AI can learn effectively.',
        confirmButtonColor: '#16a34a'
      });
      return;
    }

    this.isSubmitting = true;

    // Assemble arrays
    const payload = {
      ...this.formData,
      gad7_items: [],
      phq9_items: []
    };

    for (let i = 1; i <= 7; i++) {
      payload.gad7_items.push(this.formData[`gad_${i}`] || 0);
    }
    for (let i = 1; i <= 9; i++) {
      payload.phq9_items.push(this.formData[`phq_${i}`] || 0);
    }

    this.kbService.submitSurvey(payload).subscribe({
      next: (res: any) => {
        this.isSubmitting = false;
        if (res.success) {
          this.isSubmitted = true;
          this.submissionRef = res.session_id || 'N/A';
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          Swal.fire('Notice', res.message || 'Could not save your responses', 'error');
        }
      },
      error: (err: any) => {
        this.isSubmitting = false;
        console.error('Questionnaire error:', err);
        Swal.fire('Error', 'Unable to reach the server. Please check your connection.', 'error');
      }
    });
  }

  resetForm(): void {
    window.location.reload();
  }
}
